import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock, Mail, ArrowLeft } from 'lucide-react';

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/admin/dashboard');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1120] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <button
          data-testid="back-to-home-btn"
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#94A3B8] hover:text-[#D4AF37] transition-colors mb-8 font-['Outfit']"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </button>

        <div className="bg-[#0F172A] border border-white/10 rounded-lg p-8 shadow-2xl shadow-black/40">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-light tracking-tight text-[#F8FAFC] font-['Cormorant_Garamond']">
              Admin Portal
            </h1>
            <p className="text-[#94A3B8] mt-2 text-sm font-['Outfit']">Khan Complex Management</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="text-[#94A3B8] text-xs tracking-[0.2em] uppercase font-bold mb-2 block">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input
                  data-testid="admin-email-input"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="admin@khancomplex.com"
                  className="pl-10 bg-[#1E293B] border-white/10 text-[#F8FAFC] placeholder:text-[#475569] focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                  required
                />
              </div>
            </div>
            <div>
              <Label className="text-[#94A3B8] text-xs tracking-[0.2em] uppercase font-bold mb-2 block">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input
                  data-testid="admin-password-input"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="pl-10 bg-[#1E293B] border-white/10 text-[#F8FAFC] placeholder:text-[#475569] focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                  required
                />
              </div>
            </div>
            <Button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-[#D4AF37] text-[#020617] font-semibold hover:bg-[#FCE69C] h-11 transition-all duration-300"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
